import {
  initializeFaro,
  getWebInstrumentations,
  LogLevel,
} from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import packageJson from "../../package.json";

const requireEnv = (name) => {
  const value = process.env[name]?.trim();

  if (!value) {
    throw new Error(`${name} is required for Faro observability`);
  }

  return value;
};

const SERVICE_NAME = requireEnv("REACT_APP_FARO_SERVICE_NAME");
const ORG_NAME = requireEnv("REACT_APP_FARO_ORG");
const COLLECTOR_URL = requireEnv("REACT_APP_FARO_COLLECTOR_URL");
const ENV = requireEnv("REACT_APP_FARO_ENV");
const SERVER_URL = process.env.REACT_APP_SERVER_URL?.trim();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.trim();

const telemetryAttributes = {
  service: SERVICE_NAME,
  org: ORG_NAME,
  environment: ENV,
};

let faro;

const createTraceUrlPattern = (url) => {
  try {
    const parsed = new URL(url);
    return new RegExp(parsed.host.replace(/\./g, "\\."));
  } catch {
    throw new Error(`Invalid Faro trace propagation URL: ${url}`);
  }
};

const propagateUrls = [SERVER_URL, BACKEND_URL]
  .filter(Boolean)
  .map(createTraceUrlPattern);

faro = initializeFaro({
  url: COLLECTOR_URL,

  app: {
    name: SERVICE_NAME,
    namespace: ORG_NAME,
    version: packageJson.version,
    environment: ENV,
  },

  sessionTracking: {
    enabled: true,
    persistent: true,
  },

  instrumentations: [
    ...getWebInstrumentations({
      captureConsole: true,
      captureConsoleDisabledLevels: [],
      captureWebVitalsAttribution: true,
    }),

    new TracingInstrumentation({
      instrumentationOptions: {
        propagateTraceHeaderCorsUrls: propagateUrls,
        fetchInstrumentationOptions: {
          ignoreNetworkEvents: false,
        },
        xhrInstrumentationOptions: {
          ignoreNetworkEvents: false,
        },
      },
    }),
  ],
});

export const setFaroUser = (user) => {
  if (!user) return;
  faro.api.setUser({
    id: String(user.id),
    username: user.details?.name || "",
    email: user.email || "",
    attributes: {
      ...telemetryAttributes,
      organisationId: String(user.organisation?.id || ""),
      organisationName: user.organisation?.name || "",
    },
  });
};

export const clearFaroUser = () => {
  faro.api.resetUser();
};

export const pushFaroEvent = (name, attributes = {}, domain = "") => {
  faro.api.pushEvent(name, { ...telemetryAttributes, ...attributes }, domain);
};

export const pushFaroError = (error, context = {}) => {
  faro.api.pushError(error, {
    context: { ...telemetryAttributes, ...context },
  });
};

export const pushFaroMeasurement = (type, values, context = {}) => {
  faro.api.pushMeasurement(
    { type, values },
    { context: { ...telemetryAttributes, ...context } }
  );
};

export const pushFaroLog = (message, level = LogLevel.INFO, context = {}) => {
  faro.api.pushLog([message], {
    level,
    context: { ...telemetryAttributes, ...context },
  });
};

export const setFaroView = (viewName) => {
  faro.api.setView({ name: viewName });
};

export default faro;
